import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Come si collega il connettore, passo per passo, dentro l'app.
 *
 * Sta qui e non in un documento a parte per una ragione pratica: il passo 1 è
 * «copia questo indirizzo», e l'indirizzo lo conosce solo il server. Una guida
 * altrove costringerebbe il coach a tenere aperte due finestre e a trascrivere
 * a mano proprio la stringa che non deve sbagliare.
 *
 * ATTENZIONE A CHI AGGIORNA: i percorsi nelle interfacce di Claude e ChatGPT
 * cambiano spesso, e una guida sbagliata è peggio di nessuna guida — manda il
 * coach a cercare voci di menu che non esistono. Questi passi sono verificati
 * il 2026-10-21 sulla documentazione ufficiale dei due fornitori
 * (support.claude.com e help.openai.com). Prima di correggerli a memoria,
 * rileggere la fonte: l'ultima revisione ha dovuto disfare passi scritti a
 * intuito che erano sbagliati per entrambi i client.
 */

const CLIENTS = [
	{
		id: "claude",
		label: "Claude",
		plans:
			"Funziona su Free, Pro, Max, Team ed Enterprise. Con l'account Free puoi tenere un solo connettore.",
		steps: [
			"Apri claude.ai dal browser (da Claude Desktop è identico: il collegamento parte comunque dal cloud di Anthropic).",
			"Vai su Customize → Connettori.",
			"Premi «+ Add» e scegli «Add custom connector».",
			"Dai un nome al connettore, per esempio Performance PRO Coach.",
			"Incolla l'indirizzo che hai copiato qui sopra e premi «Continue».",
			"Rivedi le impostazioni di autenticazione e premi «Continue».",
			"Come metodo scegli «Sign in now», e come client OAuth «Register automatically».",
			"Premi «Add». Si apre la pagina di Performance PRO Coach che chiede il permesso: entra con il tuo account coach, scegli cosa concedere e conferma.",
		],
		note: "Se sei su Team o Enterprise il connettore lo aggiunge il proprietario dell'organizzazione da Organization settings → Connectors; poi ciascuno lo attiva dal proprio Customize → Connettori.",
		tail: "Apri una conversazione nuova: quelle già iniziate non vedono i connettori aggiunti dopo.",
	},
	{
		id: "chatgpt",
		label: "ChatGPT",
		plans:
			"Serve ChatGPT dal browser: le app desktop e mobile non supportano i server MCP personalizzati. Non serve più attivare la Developer Mode.",
		steps: [
			"Apri chatgpt.com dal browser.",
			"Vai nella sezione Plugins (chatgpt.com/plugins).",
			"Premi il pulsante «+» e scegli «Add custom MCP server».",
			"Dai un nome al server; la descrizione è facoltativa.",
			"Sotto «Connection» incolla l'indirizzo che hai copiato qui sopra.",
			"Come autenticazione scegli OAuth.",
			"Spunta «I understand and want to continue» per sbloccare il pulsante di creazione.",
			"Premi «Create as a plugin» e controlla l'elenco degli strumenti trovati.",
		],
		note: "Durante la creazione ChatGPT ti porta sulla pagina del permesso di Performance PRO Coach: entra con il tuo account coach, scegli cosa concedere e conferma.",
		tail: "In chat scrivi «@» nella casella del messaggio e scegli il plugin. Evita le chat temporanee: non tengono i connettori.",
	},
];

// I quattro permessi come li vede il coach sulla pagina del consenso. Le stesse
// etichette stanno in lib/mcp.js, che le usa per descrivere i collegamenti già
// attivi: qui servono *prima*, per decidere cosa concedere.
const SCOPES = [
	["Leggere", "atleti, risultati, esercizi, programmi, calendario gare"],
	["Creare programmi", "nuovi programmi, settimane, giorni, esercizi"],
	["Cancellare dai programmi", "togliere esercizi, giorni, settimane"],
	["Scrivere le gare", "aggiungere e modificare gare nel calendario atleti"],
];

const EXAMPLES = [
	"Guarda gli ultimi risultati di Marco e preparami quattro settimane di forza sulla panca, tre sedute a settimana, da lunedì prossimo.",
	"Nel programma di Giulia sostituisci lo squat del mercoledì con un front squat e abbassa il carico del 10%.",
	"Che gare ha in calendario la squadra nei prossimi due mesi?",
];

function Section({ title, children }) {
	return (
		<div className="space-y-1.5 border-t border-border pt-3">
			<p className="text-xs uppercase tracking-wide text-muted-foreground">
				{title}
			</p>
			{children}
		</div>
	);
}

export function McpSetupGuide() {
	const [open, setOpen] = useState(false);
	const [client, setClient] = useState("claude");
	const active = CLIENTS.find((item) => item.id === client);

	return (
		<div className="rounded-sm border border-border">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				aria-expanded={open}
				className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-secondary/40"
			>
				Come si collega, passo per passo
				<ChevronDown
					className={cn(
						"h-4 w-4 shrink-0 text-muted-foreground transition-transform",
						open && "rotate-180",
					)}
				/>
			</button>

			{open && (
				<div className="space-y-3 border-t border-border p-3">
					<div className="flex gap-1" role="tablist">
						{CLIENTS.map((item) => (
							<button
								key={item.id}
								type="button"
								role="tab"
								aria-selected={client === item.id}
								onClick={() => setClient(item.id)}
								className={cn(
									"rounded-sm border px-3 py-1 text-xs",
									client === item.id
										? "border-volt bg-volt/10 text-foreground"
										: "border-border text-muted-foreground hover:text-foreground",
								)}
							>
								{item.label}
								{item.recommended && (
									<span className="ml-1.5 text-[10px] uppercase tracking-wide text-volt">
										consigliato
									</span>
								)}
							</button>
						))}
					</div>

					<p className="text-xs text-muted-foreground">{active.plans}</p>

					{active.warning && (
						<p className="flex items-start gap-1.5 rounded-sm border border-blaze/40 bg-blaze/10 p-2 text-xs">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blaze" />
							<span>{active.warning}</span>
						</p>
					)}

					<ol className="list-decimal space-y-1.5 pl-5 text-sm">
						{active.steps.map((step) => (
							<li key={step}>{step}</li>
						))}
					</ol>

					<p className="text-xs text-muted-foreground">{active.note}</p>
					<p className="text-xs text-muted-foreground">{active.tail}</p>

					<Section title="Quali permessi concedere">
						<ul className="space-y-1 text-xs">
							{SCOPES.map(([label, detail]) => (
								<li key={label}>
									<span className="font-medium">{label}</span>
									<span className="text-muted-foreground"> — {detail}</span>
								</li>
							))}
						</ul>
						<p className="text-xs text-muted-foreground">
							La prima volta dai <strong>lettura + creazione</strong>: la
							cancellazione aggiungila quando ti sarai fatto un'idea di come
							lavora. Quello che non concedi, l'assistente non lo vede nemmeno
							fra i suoi strumenti.
						</p>
					</Section>

					<Section title="Esempi di richiesta">
						<ul className="space-y-1.5 text-xs text-muted-foreground">
							{EXAMPLES.map((example) => (
								<li key={example} className="border-l-2 border-border pl-2">
									{example}
								</li>
							))}
						</ul>
						<Link to="/ai-guide" className="text-xs text-volt hover:underline">
							Tutti gli esempi, per area, nella Guida AI →
						</Link>
					</Section>

					<Section title="Se qualcosa non va">
						<ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
							<li>
								<strong>Non riesce a collegarsi:</strong> ricopia l'indirizzo
								dall'app invece di riscriverlo — niente spazi, niente barra
								finale.
							</li>
							<li>
								<strong>Dice che l'account non è autorizzato:</strong> il
								connettore è riservato agli account coach con l'assistente
								abilitato. Scrivici e lo attiviamo.
							</li>
							<li>
								<strong>Il connettore non compare in chat:</strong> su Claude
								apri una conversazione nuova; su ChatGPT richiamalo con «@» e
								non usare le chat temporanee.
							</li>
							<li>
								<strong>Su ChatGPT legge ma non scrive:</strong> è un limite del
								piano, non un errore. In quel caso i programmi si fanno scrivere
								da Claude.
							</li>
							<li>
								<strong>Funzionava e ha smesso:</strong> se ti sei collegato
								prima del 20 ottobre 2026 l'indirizzo è cambiato. Scollega e
								rifai il collegamento con quello qui sopra.
							</li>
							<li>
								<strong>Dice di non avere il permesso:</strong> scollega e
								ricollega concedendo quello che gli serve.
							</li>
						</ul>
					</Section>
				</div>
			)}
		</div>
	);
}
